// phoneFormat.ts — shared phone display + canonical helpers.
//
// Apr 28, 2026: introduced alongside the homeowner_phone backfill to E.164.
// Storage is now canonical "+1XXXXXXXXXX"; display layers should call
// `formatPhone()` to render "(XXX) XXX-XXXX" for staff and homeowners.
// Use `toE164()` whenever a value is being saved or compared.

/**
 * Render any phone string in human-readable "(XXX) XXX-XXXX" form.
 * Handles E.164 (+1XXXXXXXXXX), digits-only (XXXXXXXXXX), parens-formatted,
 * dash-formatted, dot-formatted, etc.
 *
 * Returns the raw string unchanged if it doesn't have a clean 10-digit core
 * (e.g. international numbers, junk data) so we never silently mangle.
 * Returns the empty string for null/undefined/empty input.
 */
export function formatPhone(raw: string | null | undefined): string {
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");
  // Strip leading country code "1" if present (treat 11-digit US numbers as 10).
  const ten = digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits;
  if (ten.length !== 10) return raw;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

/**
 * Convert any phone-ish string to canonical E.164 ("+1XXXXXXXXXX").
 * Returns null when the input doesn't have 10 digits (or 11 starting with 1).
 * Use this when SAVING to deals.homeowner_phone or sending to Twilio.
 */
export function toE164(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}
